import { NoticeTypes, UI, Stack, VStack } from '@genial/components';
import { useFlags } from '@unleash/proxy-client-react';
import React, { useEffect, useState } from 'react';

import { NoticesAccordion } from '../../../../../../components/NoticesAccordion';
import { FeatureToggle } from '../../../../../../utils/useUnleashFlags';
import { useGlobalAssetContext } from '../../contexts';

import { MobileNotices } from './components/MobileNotices';

export interface AssetNoticesNormalized {
  id: string;
  ableToClose: boolean;
  type: NoticeTypes.Props['type'];
  textObjects: NoticeTypes.Props['textObjects'];
  backgroundColor?: NoticeTypes.Props['backgroundColor'];
  unleashFlag?: FeatureToggle;
  batchTicketOnly?: boolean;
}

export const AssetNotices = () => {
  const flags = useFlags();

  const [hidedNotices, setHidedNotices] = useState<string[]>([]);
  const [visibleNotices, setVisibleNotices] = useState<AssetNoticesNormalized[]>([]);

  const { selectedTab, isMobile, isBatchTicket } = useGlobalAssetContext();

  const { notices, id: tabId, groupNotices } = selectedTab;

  const getNoticeId = (notice: AssetNoticesNormalized) => `${tabId}-${notice.id}`;

  const handleHideNotice = (index: number, notice: AssetNoticesNormalized) => {
    setHidedNotices((prevHidedNotices) => [...prevHidedNotices, getNoticeId(notice)]);
    setVisibleNotices((prevNotices) => {
      const newNotices = [...prevNotices];
      newNotices.splice(index, 1);
      return newNotices;
    });
  };

  const hasFeatureFlagEnabled = (notice: AssetNoticesNormalized) => {
    if (!notice.unleashFlag) {
      return true;
    }

    return !!flags.find((flag) => flag.name === notice.unleashFlag)?.enabled;
  };

  const isNoticeEnabledForFlow = (notice: AssetNoticesNormalized) => {
    if (!notice.batchTicketOnly) {
      return true;
    }

    return isBatchTicket;
  };

  useEffect(() => {
    setVisibleNotices(
      notices.filter((notice) => {
        return (
          hasFeatureFlagEnabled(notice) &&
          isNoticeEnabledForFlow(notice) &&
          !hidedNotices.includes(getNoticeId(notice))
        );
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notices, isBatchTicket]);

  if (!visibleNotices.length) {
    return <></>;
  }

  if (isMobile) {
    return <MobileNotices notices={visibleNotices} />;
  }

  if (groupNotices) {
    return (
      <VStack space="stack.xxxs" maxW="full" w="full" pt="stack.xxs">
        <NoticesAccordion notices={visibleNotices} handleHideNotice={handleHideNotice} />
      </VStack>
    );
  }

  return (
    <VStack space="stack.xxxs" maxW="full" w="full" pt="stack.xxs">
      {visibleNotices.map((notice, index) => (
        <Stack key={`${tabId}-notice-${index}`} maxW="full" w="full">
          <UI.Notice
            testID={`${tabId}-notice-${index}`}
            {...notice}
            {...(notice.ableToClose && { onPress: () => handleHideNotice(index, notice) })}
          />
        </Stack>
      ))}
    </VStack>
  );
};
